"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { LegalNote } from "@/components/LegalNote";
import { TextSizeButton } from "@/components/TextSizeButton";
import { IconPlay } from "@/components/icons";
import { Mascot } from "@/components/Mascot";
import { MODES, ModeStage } from "@/components/ModeScene";
import { ApiError, api, storeToken } from "@/lib/api";

type Mode = "create" | "join";

/**
 * The front door, composed like the game and not like a website: one console
 * on the stage, the pitch and the form inside the same frame (spec §10a).
 */
export default function Home() {
  const router = useRouter();
  const [mode, setMode] = useState<Mode>("create");
  const [nickname, setNickname] = useState("");

  // The name they answered to last time is the name the field starts with.
  useEffect(() => setNickname(window.localStorage.getItem("parroty.name") ?? ""), []);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result =
        mode === "create"
          ? await api.createRoom(nickname.trim())
          : await api.joinRoom(code.trim().toUpperCase(), nickname.trim());

      window.localStorage.setItem("parroty.name", nickname.trim());
      storeToken(result.player.session_token, result.code);
      router.push(`/room/${result.code}`);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? (cause.fieldError("nickname") ?? cause.fieldError("code") ?? cause.message)
          : "Не удалось связаться с сервером.",
      );
      setBusy(false);
    }
  }

  const field =
    "w-full rounded-2xl border-4 border-outline bg-card px-4 py-3.5 text-lg text-ink outline-none transition-colors duration-150 placeholder:text-ink-faint hover:border-sky focus:border-sky focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-sky";

  return (
    <main className="studio-set flex min-h-dvh flex-col px-4 py-5 selection:bg-sky selection:text-[#12162c] sm:px-6">
      <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center gap-4">
        <section className="sticker rounded-[2rem] border-4 border-outline bg-card p-5 sm:p-8">
          {/* The console header: the show's own masthead. */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b-4 border-card-sunk pb-5">
            <div className="flex items-center gap-2.5">
              <Mascot state="counting" className="h-11 w-11 shrink-0" />
              <span className="font-display text-2xl font-extrabold tracking-tight text-ink">
                Parroty
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full border-4 border-outline bg-card-sunk px-3 py-1.5 text-[0.66rem] font-bold uppercase tracking-[0.14em] text-ink-soft">
                студия дубляжа · 1–16 игроков
              </span>
              <TextSizeButton />
            </div>
          </div>

          <div className="mt-6 grid items-center gap-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12">
            <div className="flex flex-col items-center gap-5 text-center">
              {/* The host opens the show: the parrot centre-stage, the title
                  card beneath. */}
              <Mascot state="idle" className="rise h-24 w-24 sm:h-28 sm:w-28" />
              {/* Exactly two lines, each pinned whole: a slogan that wraps
                  into four is a paragraph. */}
              <h1 className="rise font-display text-[clamp(1.7rem,3.6vw,2.9rem)] font-extrabold leading-[1.04] tracking-[-0.03em] text-ink">
                <span className="block whitespace-nowrap">Переозвучьте мемы</span>
                <span className="block whitespace-nowrap text-sky-light">вместе с друзьями</span>
              </h1>

              <p className="rise rise-2 max-w-[44ch] text-lg leading-relaxed text-ink-soft">
                Каждому достаются свои реплики. Три-два-один — пишете{" "}
                <span className="font-bold text-ink">все разом</span>, а потом
                смотрите сцену, которая заговорила вашими голосами.
              </p>

            </div>

            <div className="rise rise-4 rounded-3xl border-4 border-card-sunk p-4 sm:p-5">
              <div className="mb-4 grid grid-cols-2 gap-2 rounded-2xl border-4 border-outline bg-card-sunk p-1.5">
                {(
                  [
                    ["create", "Собрать комнату"],
                    ["join", "Войти по коду"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={mode === value}
                    onClick={() => {
                      setMode(value);
                      setError(null);
                    }}
                    className={`cursor-pointer rounded-xl px-3 py-2.5 font-display text-sm font-bold transition-all duration-150 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-sky active:scale-95 ${
                      mode === value ? "bg-sky" : "text-ink-soft hover:text-ink"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-ink-soft">
                    Как вас объявить
                  </span>
                  <input
                    value={nickname}
                    onChange={(event) => setNickname(event.target.value)}
                    placeholder="Как вас звать"
                    maxLength={24}
                    required
                    className={field}
                  />
                </label>

                {/*
                  Always in the layout, invisible when unused: the tabs must
                  not change the console's height, or switching them jerks the
                  whole page.
                */}
                <label
                  aria-hidden={mode !== "join"}
                  className={`flex flex-col gap-1.5 transition-opacity duration-150 ${
                    mode === "join" ? "" : "pointer-events-none opacity-35"
                  }`}
                >
                  <span className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-ink-soft">
                    {mode === "join" ? "Код комнаты" : "Код комнаты — появится после создания"}
                  </span>
                  {/* Identical classes in both modes: a font-size swap here is
                      a height swap, and the console must not move. */}
                  <input
                    value={mode === "join" ? code : ""}
                    onChange={(event) => setCode(event.target.value.toUpperCase())}
                    placeholder="????????"
                    maxLength={8}
                    required={mode === "join"}
                    disabled={mode !== "join"}
                    tabIndex={mode === "join" ? 0 : -1}
                    autoComplete="off"
                    spellCheck={false}
                    className={`${field} text-center font-mono uppercase tracking-[0.3em]`}
                  />
                </label>

                {error && (
                  <p
                    role="alert"
                    aria-live="polite"
                    className="rounded-2xl border-4 border-outline bg-onair/15 px-4 py-3 text-sm font-medium leading-snug text-ink"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={busy || nickname.trim().length === 0}
                  className="press sticker mt-1 cursor-pointer rounded-2xl border-4 border-outline bg-sun px-5 py-4 font-display text-lg font-extrabold tracking-tight text-ink hover:bg-amber focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-sky disabled:cursor-not-allowed disabled:bg-[#2a3162] disabled:text-ink-soft disabled:shadow-none"
                >
                  <span className="inline-flex items-center justify-center gap-2.5">
                    {!busy && <IconPlay className="h-5 w-5" />}
                    {busy ? "Секунду…" : mode === "create" ? "Открыть студию" : "Войти в студию"}
                  </span>
                </button>

                <p className="text-center text-xs leading-relaxed text-ink-faint">
                  Зверя выдадим на входе, микрофон проверим до начала. Играете
                  своими паками — своими мемами.
                </p>

                <div className="flex justify-center">
                  <LegalNote />
                </div>
              </form>
            </div>
          </div>

          {/* The three games get the console's full width: scenes first,
              names beneath, one aligned row. */}
          {/* A list of three games, not a definition list: dt/dd wrapped in
              divs is not a structure a screen reader can walk. */}
          <ul className="rise rise-3 mt-6 grid gap-3.5 sm:grid-cols-3">
            {MODES.map(({ id, name, blurb, tone, scene }, index) => (
              <li
                key={id}
                className={`group sticker-sm flex flex-col gap-3 rounded-3xl border-4 border-outline p-4 transition-transform duration-150 ease-out hover:-translate-y-1 ${
                  index % 2 ? "hover:rotate-1" : "hover:-rotate-1"
                } ${tone}`}
              >
                <ModeStage scene={scene} />
                <div className="px-1">
                  <h2 className="font-display text-lg font-extrabold tracking-tight">{name}</h2>
                  <p className="mt-0.5 text-sm leading-snug opacity-90">{blurb}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}
